import { useMemo, useState } from 'react'
import { Box, Breadcrumbs, Typography, Stack, TextField, MenuItem, Autocomplete, Button, Alert, CircularProgress, Paper, Tabs, Tab, Chip, IconButton, Snackbar } from '@mui/material'
import { Add, CheckCircleOutlined, Cancel } from '@mui/icons-material'
import { DataGrid, type GridColDef } from '@mui/x-data-grid'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { transferStock, listStockTransfers, confirmStockTransfer, cancelStockTransfer, type StockTransferListItem, type StockTransferStatus } from '../../api/stocuri'
import { listArticles } from '../../api/nomenclatoare'
import { listWarehouses, getWarehouse } from '../../api/depozite'
import { errorMessage } from '../../api/client'
import StockTransferFormDialog from './StockTransferFormDialog'

const STATUS_LABEL: Record<StockTransferStatus, string> = { InTranzit: 'În tranzit', Receptionat: 'Recepționat', Anulat: 'Anulat' }
const STATUS_COLOR: Record<StockTransferStatus, 'warning' | 'success' | 'default'> = { InTranzit: 'warning', Receptionat: 'success', Anulat: 'default' }

export default function TransfersPage() {
  const [tab, setTab] = useState<'zone' | 'puncte-lucru'>('zone')

  return (
    <Stack spacing={2}>
      <Breadcrumbs separator="/" sx={{ fontSize: 12.5 }}>
        <Typography variant="inherit" color="text.secondary">Stocuri</Typography>
        <Typography variant="inherit" color="text.primary">Transferuri</Typography>
      </Breadcrumbs>
      <Typography variant="h2">Transferuri</Typography>
      <Tabs value={tab} onChange={(_, v) => setTab(v)}>
        <Tab value="zone" label="Între zone / locații" />
        <Tab value="puncte-lucru" label="Între puncte de lucru" />
      </Tabs>
      {tab === 'zone' ? <ZoneTransferForm /> : <WorkPointTransfers />}
    </Stack>
  )
}

function ZoneTransferForm() {
  const qc = useQueryClient()
  const [articleId, setArticleId] = useState<string | null>(null)
  const [warehouseId, setWarehouseId] = useState<string | null>(null)
  const [sourceZoneId, setSourceZoneId] = useState('')
  const [sourceLocationId, setSourceLocationId] = useState('')
  const [destinationZoneId, setDestinationZoneId] = useState('')
  const [destinationLocationId, setDestinationLocationId] = useState('')
  const [lotNumber, setLotNumber] = useState('')
  const [quantity, setQuantity] = useState('')
  const [reference, setReference] = useState('')

  const { data: articles } = useQuery({ queryKey: ['nomenclatoare', 'articole', 'toate'], queryFn: () => listArticles() })
  const { data: warehouses } = useQuery({ queryKey: ['stocuri', 'depozite'], queryFn: listWarehouses })
  const { data: warehouseDetail } = useQuery({ queryKey: ['stocuri', 'depozit', warehouseId ?? null], queryFn: () => getWarehouse(warehouseId!), enabled: !!warehouseId })

  const sourceZone = warehouseDetail?.zones.find((z) => z.id === sourceZoneId)
  const destinationZone = warehouseDetail?.zones.find((z) => z.id === destinationZoneId)

  const mutation = useMutation({
    mutationFn: () => transferStock({
      articleId: articleId!, warehouseId: warehouseId!, sourceZoneId: sourceZoneId || null, sourceLocationId: sourceLocationId || null,
      destinationZoneId: destinationZoneId || null, destinationLocationId: destinationLocationId || null,
      lotNumber: lotNumber || null, quantity: Number(quantity), reference: reference || null,
    }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['stocuri'] })
      setQuantity(''); setReference('')
    },
  })

  const canSubmit = !!articleId && !!warehouseId && !!quantity

  return (
    <Paper variant="outlined" sx={{ p: 3, maxWidth: 720 }}>
      <Stack spacing={2}>
        <Stack direction="row" spacing={2}>
          <Autocomplete
            sx={{ flexGrow: 1 }} options={articles?.map((a) => a.id) ?? []}
            getOptionLabel={(id) => { const a = articles?.find((x) => x.id === id); return a ? `${a.code} — ${a.shortDescription}` : id }}
            value={articleId} onChange={(_, v) => setArticleId(v)}
            renderInput={(params) => <TextField {...params} label="Articol" />}
          />
          <Autocomplete
            sx={{ flexGrow: 1 }} options={warehouses?.map((w) => w.id) ?? []}
            getOptionLabel={(id) => warehouses?.find((w) => w.id === id)?.name ?? id}
            value={warehouseId} onChange={(_, v) => { setWarehouseId(v); setSourceZoneId(''); setSourceLocationId(''); setDestinationZoneId(''); setDestinationLocationId('') }}
            renderInput={(params) => <TextField {...params} label="Depozit" />}
          />
        </Stack>
        <Typography variant="subtitle2">Sursă</Typography>
        <Stack direction="row" spacing={2}>
          <TextField select label="Zonă sursă" sx={{ flexGrow: 1 }} value={sourceZoneId} onChange={(e) => { setSourceZoneId(e.target.value); setSourceLocationId('') }} disabled={!warehouseId}>
            <MenuItem value="">—</MenuItem>
            {warehouseDetail?.zones.map((z) => <MenuItem key={z.id} value={z.id!}>{z.name}</MenuItem>)}
          </TextField>
          <TextField select label="Locație sursă" sx={{ flexGrow: 1 }} value={sourceLocationId} onChange={(e) => setSourceLocationId(e.target.value)} disabled={!sourceZone}>
            <MenuItem value="">—</MenuItem>
            {sourceZone?.locations.map((l) => <MenuItem key={l.id} value={l.id!}>{l.code}</MenuItem>)}
          </TextField>
        </Stack>
        <Typography variant="subtitle2">Destinație</Typography>
        <Stack direction="row" spacing={2}>
          <TextField select label="Zonă destinație" sx={{ flexGrow: 1 }} value={destinationZoneId} onChange={(e) => { setDestinationZoneId(e.target.value); setDestinationLocationId('') }} disabled={!warehouseId}>
            <MenuItem value="">—</MenuItem>
            {warehouseDetail?.zones.map((z) => <MenuItem key={z.id} value={z.id!}>{z.name}</MenuItem>)}
          </TextField>
          <TextField select label="Locație destinație" sx={{ flexGrow: 1 }} value={destinationLocationId} onChange={(e) => setDestinationLocationId(e.target.value)} disabled={!destinationZone}>
            <MenuItem value="">—</MenuItem>
            {destinationZone?.locations.map((l) => <MenuItem key={l.id} value={l.id!}>{l.code}</MenuItem>)}
          </TextField>
        </Stack>
        <Stack direction="row" spacing={2}>
          <TextField label="Lot (opțional)" value={lotNumber} onChange={(e) => setLotNumber(e.target.value)} sx={{ flexGrow: 1 }} />
          <TextField type="number" label="Cantitate" value={quantity} onChange={(e) => setQuantity(e.target.value)} sx={{ width: 160 }} />
        </Stack>
        <TextField label="Referință (opțional)" value={reference} onChange={(e) => setReference(e.target.value)} fullWidth />
        {mutation.isError && <Alert severity="error">{errorMessage(mutation.error)}</Alert>}
        {mutation.isSuccess && <Alert severity="success">Transfer înregistrat.</Alert>}
        <Box>
          <Button variant="contained" disabled={!canSubmit || mutation.isPending} onClick={() => mutation.mutate()}
            startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}>
            Transferă
          </Button>
        </Box>
      </Stack>
    </Paper>
  )
}

/** FR-ADM-003b — transfer de stoc între puncte de lucru, cu document, stoc în tranzit și confirmare la destinație. */
function WorkPointTransfers() {
  const [formOpen, setFormOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({ queryKey: ['stocuri', 'transferuri-stoc'], queryFn: listStockTransfers })

  const invalidate = () => void qc.invalidateQueries({ queryKey: ['stocuri', 'transferuri-stoc'] })
  const confirmMutation = useMutation({ mutationFn: confirmStockTransfer, onSuccess: invalidate, onError: (e) => setError(errorMessage(e)) })
  const cancelMutation = useMutation({ mutationFn: cancelStockTransfer, onSuccess: invalidate, onError: (e) => setError(errorMessage(e)) })

  const columns = useMemo<GridColDef<StockTransferListItem>[]>(() => [
    { field: 'number', headerName: 'Nr.', width: 130, renderCell: (p) => <code>{p.row.number}</code> },
    { field: 'transferDate', headerName: 'Data', width: 110 },
    { field: 'sourceWarehouseName', headerName: 'Depozit sursă', flex: 1, minWidth: 160 },
    { field: 'destinationWarehouseName', headerName: 'Depozit destinație', flex: 1, minWidth: 160 },
    {
      field: 'status', headerName: 'Status', width: 140, sortable: false,
      renderCell: (p) => <Chip size="small" label={STATUS_LABEL[p.row.status]} color={STATUS_COLOR[p.row.status]} variant="outlined" />,
    },
    {
      field: 'actions', headerName: '', width: 100, sortable: false, filterable: false, disableColumnMenu: true,
      renderCell: (p) => p.row.status === 'InTranzit' ? (
        <Stack direction="row">
          <IconButton size="small" title="Confirmă recepția" onClick={() => confirmMutation.mutate(p.row.id)}>
            <CheckCircleOutlined fontSize="small" color="success" />
          </IconButton>
          <IconButton size="small" title="Anulează" onClick={() => cancelMutation.mutate(p.row.id)}>
            <Cancel fontSize="small" color="error" />
          </IconButton>
        </Stack>
      ) : null,
    },
  ], [confirmMutation, cancelMutation])

  return (
    <Stack spacing={2}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" startIcon={<Add />} onClick={() => setFormOpen(true)}>Transfer nou</Button>
      </Box>
      <Box sx={{ height: 480, bgcolor: 'background.paper', borderRadius: 1.5 }}>
        <DataGrid rows={data ?? []} columns={columns} loading={isLoading} density="comfortable" disableRowSelectionOnClick
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }} pageSizeOptions={[25, 50]} />
      </Box>
      <StockTransferFormDialog open={formOpen} onClose={() => setFormOpen(false)} />
      <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError(null)}>
        <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
      </Snackbar>
    </Stack>
  )
}
